import { createApi } from "@reduxjs/toolkit/query/react";
import {
  CreateUpdateAdditionalDetailsFormPayload,
  Type,
} from "@src/pages/task-manager/additional-details-form/AdditionalDetailsForm.types";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import { AdminPayload } from "@src/pages/super-admin/org-registration/OrgRegistration.types";

// type AdditionalDetails = {
//   [key: string]: {
//     id: number;
//     type: Type;
//     isMandatory: boolean;
//     options?: string[];
//     inputType?: string;
//     isDOB?: boolean;
//   };
// };

interface GetAllOrgCategoryTemplatesResponse extends APIResponse {
  response: {
    content: { id: string; orgCategory: string }[];
  };
}

export interface AdditionalDetailField {
  id?: number;
  type: string;
  isMandatory: boolean;
  predefined?: boolean;
  inputType?: string;
  options?: string[];
}

export type AdditionalDetails = Record<string, AdditionalDetailField>;
export type StudentOrgData = Record<string, string>;

export interface GetVersionChangeAdditionalDetailsResponse {
  statusCode: number;
  httpStatus: string;
  response: {
    organizationId: string;
    additionalDetails: AdditionalDetails;
    studentOrgData: StudentOrgData;
  };
}

interface GetCategoeryByIdResponse extends APIResponse {
  response: {
    id: string;
    orgCategory: string;
    additionalDetails: AdditionalDetails | null;
  };
}

interface GetOrgCategoryTemplate extends APIResponse {
  response: {
    orgCategory: string;
    organizationId: string;
    organizationName: string;
    additionalDetails: AdditionalDetails;
  };
}

interface CreateOrgCategoryTemplateResponse extends APIResponse {
  response: {
    id: string;
    orgCategory?: string;
    additionalDetails?: AdditionalDetails | null;
  };
}

interface UpdateOrgTemplateResponse extends APIResponse {
  response: {
    additionalDetails: AdditionalDetails;
    id: string;
    name: string;
    orgCategory: string;
    type: string;
    version: number;

    address: string;
    country: string;
    state: string;
    pinCode: string;

    phoneNumber: string;
    organizationEmail: string;
    websiteUrl: string;

    subjectIds: string[];

    createdByName: string;
    createdByEmail: string;
    createdOn: string;

    modifiedByName: string;
    modifiedByEmail: string;
    lastUpdateOn: string;
  };
}

export const RegistrationTemplateApiSlice = createApi({
  reducerPath: "registrationTemplateApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getAllOrgCategoryTemplates: builder.query<
      GetAllOrgCategoryTemplatesResponse,
      { pageNo: number; pageSize: number }
    >({
      query: ({ pageNo, pageSize }) => ({
        url: `orgCategory_template/get-all?pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getCategoryTemplateById: builder.query<
      GetCategoeryByIdResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `orgCategory_template?orgCategoryTemplateId=${id}`,
        method: "GET",
      }),
    }),
    getOrgCategoryTemplate: builder.query<
      GetOrgCategoryTemplate,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `organization/get-organization-additionalDetails?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    createOrgCategoryTemplate: builder.mutation<
      CreateOrgCategoryTemplateResponse,
      {
        payload: {
          orgCategory: string;
          additionalDetails: CreateUpdateAdditionalDetailsFormPayload;
        };
      }
    >({
      query: ({ payload }) => ({
        url: "orgCategory_template",
        method: "POST",
        body: payload,
      }),
    }),
    RegisterOrgForStudentWithAdditionalDetails: builder.mutation<
      any,
      {
        payload: { [key: string]: string | undefined };
        organizationId: string;
        subOrganizationId?: string;
        branchId?: string;
      }
    >({
      query: ({ payload, organizationId, subOrganizationId, branchId }) => {
        let url = `student/add-org-studentMetaData?organizationId=${organizationId}`;

        if (subOrganizationId) {
          url += `&subOrganizationId=${subOrganizationId}`;
        }

        if (branchId) {
          url += `&branchId=${branchId}`;
        }

        return {
          url,
          method: "POST",
          body: payload,
        };
      },
    }),
    GetVersionChangeAdditionalDetails: builder.query<
      GetVersionChangeAdditionalDetailsResponse,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `student/get-orgForm-data?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),

    updateCategoryTemplate: builder.mutation<
      CreateOrgCategoryTemplateResponse,
      {
        id: string;
        payload: {
          orgCategory: string;
          additionalDetails: CreateUpdateAdditionalDetailsFormPayload;
        };
      }
    >({
      query: ({ id, payload }) => ({
        url: `orgCategory_template?orgCategoryTemplateId=${id}`,
        method: "PUT",
        body: payload,
      }),
    }),
    updateOrgTemplate: builder.mutation<
      UpdateOrgTemplateResponse,
      {
        organizationId: string;
        payload: AdminPayload;
      }
    >({
      query: ({ organizationId, payload }) => ({
        url: `organization/add-additionalDetails?organizationId=${organizationId}`,
        method: "PATCH",
        body: payload?.additionalDetails,
      }),
    }),
  }),
});

export const {
  useGetAllOrgCategoryTemplatesQuery,
  useCreateOrgCategoryTemplateMutation,
  useLazyGetCategoryTemplateByIdQuery,
  useUpdateCategoryTemplateMutation,
  useGetOrgCategoryTemplateQuery,
  useUpdateOrgTemplateMutation,
  useRegisterOrgForStudentWithAdditionalDetailsMutation,
  useGetVersionChangeAdditionalDetailsQuery,
  useLazyGetVersionChangeAdditionalDetailsQuery,
  useLazyGetAllOrgCategoryTemplatesQuery,
} = RegistrationTemplateApiSlice;
